import { Controller } from "@hotwired/stimulus"

// Places-autocomplete for location fields, backed by /geo/suggest.
// The visible input keeps a plain display string; the hidden "place" field
// carries the structured place JSON only while the input still matches the
// picked suggestion (free-typed edits clear it so the server never stores
// stale coordinates).
export default class extends Controller {
    static targets = ["input", "place", "results"]
    static values = { url: String }

    connect() {
        this._items = []
        this._selectedIndex = -1
        this._debounceTimer = null
        this._selectedLabel = this._placeLabel()
        this._onDocClick = (event) => {
            if (!this.element.contains(event.target)) this._closeResults()
        }
        document.addEventListener("click", this._onDocClick)
    }

    disconnect() {
        document.removeEventListener("click", this._onDocClick)
        this._clearDebounce()
    }

    query() {
        this._syncPlaceWithInput()
        this._clearDebounce()
        let value = this.inputTarget.value.trim()
        if (value.length < 3) {
            this._closeResults()
            return
        }
        this._debounceTimer = window.setTimeout(() => this._fetchSuggestions(value), 300)
    }

    keydown(event) {
        if (this.resultsTarget.hidden) return
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._moveSelection(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._moveSelection(-1)
        } else if (event.key === "Enter") {
            if (this._selectedIndex >= 0) {
                event.preventDefault()
                this._pick(this._selectedIndex)
            }
        } else if (event.key === "Escape") {
            this._closeResults()
        }
    }

    blur() {
        // Delay so a click on a suggestion still lands before the list hides.
        window.setTimeout(() => this._closeResults(), 150)
    }

    async _fetchSuggestions(value) {
        try {
            let response = await fetch(`${this.urlValue}?q=${encodeURIComponent(value)}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!response.ok) throw new Error("suggest failed")
            let payload = await response.json()
            if (this.inputTarget.value.trim() !== value) return
            this._render(payload.suggestions || [])
        } catch (_error) {
            this._closeResults()
        }
    }

    _render(suggestions) {
        this._suggestions = suggestions
        this._items = []
        this._selectedIndex = -1
        this.resultsTarget.replaceChildren()
        if (suggestions.length === 0) {
            this._closeResults()
            return
        }

        suggestions.forEach((place, index) => {
            let row = document.createElement("button")
            row.type = "button"
            row.className = "location-typeahead-item"
            row.dataset.index = index.toString()
            let primary = document.createElement("span")
            primary.className = "location-typeahead-item-name"
            primary.textContent = place.name || place.label
            let secondary = document.createElement("span")
            secondary.className = "location-typeahead-item-detail"
            secondary.textContent = [place.region, place.country].filter(Boolean).join(", ")
            row.append(primary, secondary)
            row.addEventListener("mousedown", (event) => event.preventDefault())
            row.addEventListener("click", () => this._pick(index))
            row.addEventListener("mouseenter", () => this._setSelection(index))
            this.resultsTarget.appendChild(row)
            this._items.push(row)
        })

        let attribution = document.createElement("div")
        attribution.className = "location-typeahead-attribution"
        attribution.textContent = "© OpenStreetMap contributors"
        this.resultsTarget.appendChild(attribution)
        this.resultsTarget.hidden = false
    }

    _pick(index) {
        let place = (this._suggestions || [])[index]
        if (!place) return
        this.inputTarget.value = place.label
        this.placeTarget.value = JSON.stringify(place)
        this._selectedLabel = place.label
        this._closeResults()
        this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    _syncPlaceWithInput() {
        if (this._selectedLabel && this.inputTarget.value.trim() !== this._selectedLabel) {
            this.placeTarget.value = ""
            this._selectedLabel = null
        }
    }

    _placeLabel() {
        try {
            let place = JSON.parse(this.placeTarget.value || "null")
            return place && place.label ? place.label : null
        } catch (_error) {
            return null
        }
    }

    _moveSelection(delta) {
        if (this._items.length === 0) return
        let next = this._selectedIndex + delta
        if (next < 0) next = this._items.length - 1
        if (next >= this._items.length) next = 0
        this._setSelection(next)
    }

    _setSelection(index) {
        this._items.forEach((row, i) => row.classList.toggle("is-active", i === index))
        this._selectedIndex = index
    }

    _closeResults() {
        this.resultsTarget.hidden = true
        this.resultsTarget.replaceChildren()
        this._items = []
        this._selectedIndex = -1
    }

    _clearDebounce() {
        if (this._debounceTimer) window.clearTimeout(this._debounceTimer)
        this._debounceTimer = null
    }
}
